export type DashboardKpiStyle = {
  valueFormat?: "number" | "integer" | "currency" | "percent"
  decimalPlaces?: number | string
  kpiLayout?: "standard" | "compact" | "prominent"
  currency?: string
  unit?: string
  notation?: "standard" | "compact"
  scale?: "none" | "thousands" | "millions" | "billions"
  percentScale?: "points" | "ratio"
  target?: { value: number; visual: "bar" | "ring" } | null
}

export type DashboardKpiTable = {
  datasetID: string
  name: string
  headers: string[]
  rows: Array<Record<string, string | number | boolean | null>>
}

export type DashboardKpiDataset =
  | { status: "loading" | "error" | "missing"; table?: never }
  | { status: "ready"; table: DashboardKpiTable }

export function dashboardKpiRequestKey(
  widget: { id?: string; data?: Record<string, unknown>; kpi_calculation?: unknown },
  filters: Array<{ data?: { targets?: Array<{ dataset_id?: string; column?: string }>; selectedValues?: string[] } }>,
  source?: string,
) {
  const data = widget.data || {}
  return JSON.stringify({
    id: widget.id, source, calculation: widget.kpi_calculation,
    dataset: data.dataset_id, filters: data.filters, sort: data.sort, rowLimit: data.rowLimit,
    ...(widget.kpi_calculation ? {} : { metric: data.metric, aggregation: data.aggregation }),
    dashboardFilters: filters.flatMap((filter) => {
      const target = filter.data?.targets?.find((item) => item.dataset_id === data.dataset_id)
      const selected = filter.data?.selectedValues
      return target?.column && selected?.length ? [{ column: target.column, selectedValues: selected }] : []
    }),
  })
}

export function availableDashboardTables<T extends { datasetID: string }>(tables: T[], datasetIDs: Set<string> | undefined): T[] {
  return datasetIDs ? tables.filter((table) => datasetIDs.has(table.datasetID)) : []
}

// A dataset's id as the transformation output tables spell it (`transformation:<id>:table:<index>`).
export function dashboardDatasetKeys(dataset: { id: string; transformation_id?: string | null; table_index?: number | null }) {
  const keys = [dataset.id]
  if (dataset.transformation_id && Number.isInteger(dataset.table_index)) keys.push(`transformation:${dataset.transformation_id}:table:${dataset.table_index}`)
  return keys
}

// The dataset list as a filter: only a list that loaded and is non-empty filters anything. listDashboardDatasets
// turns a failed request into [], so an empty list means "unknown", never "nothing may be shown". Each dataset is
// matched on its own id and on its normalised output-table id.
export function listedDashboardDatasetIDs(
  listed: Array<{ id: string; transformation_id?: string | null; table_index?: number | null }> | undefined,
): Set<string> | undefined {
  if (!listed?.length) return undefined
  return new Set(listed.flatMap(dashboardDatasetKeys))
}

// Tables a dashboard may show: the listed ones, or every one when there is no usable list (as before the list was
// the authority).
export function listedDashboardTables<T extends { datasetID: string }>(tables: T[], listed: Set<string> | undefined): T[] {
  return listed ? tables.filter((table) => listed.has(table.datasetID)) : tables
}

const scales = {
  none: { divisor: 1, suffix: "" },
  thousands: { divisor: 1e3, suffix: "K" },
  millions: { divisor: 1e6, suffix: "M" },
  billions: { divisor: 1e9, suffix: "B" },
}

export function resolveDashboardKpi(value: number, style: DashboardKpiStyle = {}, locale?: string) {
  const format = style.valueFormat ?? "number"
  const decimals = Number(style.decimalPlaces ?? 2)
  const layout = style.kpiLayout ?? "standard"
  const currency = style.currency ?? "USD"
  const unit = style.unit ?? ""
  const percentScale = style.percentScale ?? "points"
  const scale = style.scale ?? "none"
  const notation = style.notation ?? "standard"
  const target = style.target
  if (
    !Number.isFinite(value) ||
    !["number", "integer", "currency", "percent"].includes(format) ||
    !Number.isInteger(decimals) || decimals < 0 || decimals > 6 ||
    !["standard", "compact", "prominent"].includes(layout) ||
    (format === "currency" && (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency))) ||
    typeof unit !== "string" || Array.from(unit).length > 24 || /[\p{C}]/u.test(unit) ||
    (format === "percent" && !["ratio", "points"].includes(percentScale)) ||
    (format !== "percent" && (!Object.hasOwn(scales, scale) || !["standard", "compact"].includes(notation))) ||
    (target != null && (!Number.isFinite(target.value) || target.value <= 0 || !["bar", "ring"].includes(target.visual)))
  ) return { status: "invalid" as const }

  const percent = format === "percent"
  // Percent mode is explicit. Fixed scaling takes precedence over compact notation.
  const fixed = scales[percent ? "none" : scale]
  const multiplier = percent && percentScale === "ratio" ? 100 : 1
  if (!Number.isFinite(value * multiplier) || (target && !Number.isFinite(target.value * multiplier))) return { status: "invalid" as const }
  const digits = format === "integer" ? 0 : decimals
  const options: Intl.NumberFormatOptions = {
    ...(format === "currency" ? { style: "currency", currency } : percent ? { style: "percent" } : {}),
    notation: percent || fixed.divisor !== 1 ? "standard" : notation,
    minimumFractionDigits: format === "currency" ? digits : 0,
    maximumFractionDigits: digits,
  }
  const formatter = new Intl.NumberFormat(locale, options)
  const trimmedUnit = unit.trim()
  const duplicate = (percent && trimmedUnit === "%") ||
    (fixed.suffix && [fixed.suffix.toLowerCase(), scale, scale.replace(/s$/, "")].includes(trimmedUnit.toLowerCase().replace(/^in\s+/, ""))) ||
    (format === "currency" && (trimmedUnit === currency || formatter.formatToParts(0).some((part) => part.type === "currency" && part.value === trimmedUnit)))
  const suffix = `${fixed.suffix}${trimmedUnit && !duplicate ? ` ${trimmedUnit}` : ""}`
  const display = (raw: number) => {
    const scaled = raw * multiplier / fixed.divisor
    // Keep legacy integer half-rounding and number rounding, while honoring all six decimals.
    const rounded = format === "integer" ? Math.round(scaled) : Number(scaled.toFixed(digits))
    return `${formatter.format(percent ? rounded / 100 : rounded)}${suffix}`
  }
  const progress = target ? value / target.value * 100 : undefined
  return {
    status: "ready" as const,
    rawValue: value,
    layout,
    displayValue: display(value),
    target: target ? {
      visual: target.visual,
      displayValue: display(target.value),
      percent: progress!,
      fill: Math.min(100, Math.max(0, progress!)),
      text: new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 2 }).format(progress! / 100),
    } : undefined,
  }
}

export function dashboardKpiDataStatus(table: DashboardKpiTable | undefined, data: { metric?: string; aggregation?: string }) {
  if (!table) return "missing"
  if (data.aggregation === "count") return "ready"
  const metric = data.metric
  if (!metric) return "missing"
  return table.rows.some((row) => {
    const value = row[metric]
    return (typeof value === "number" || typeof value === "string") && String(value).trim() !== "" &&
      Number.isFinite(Number(String(value).replace(/,/g, "")))
  }) ? "ready" : "missing"
}

export function aggregateDashboardKpi(table: DashboardKpiTable | undefined, data: { metric?: string; aggregation?: string }) {
  if (!table) return 0
  const metric = data.metric
  const aggregation = data.aggregation || "sum"
  // Preserve existing calculation semantics, including blank cells in mixed datasets.
  const values = metric ? table.rows.map((row) => Number(String(row[metric] ?? "").replace(/,/g, ""))).filter(Number.isFinite) : []
  if (aggregation === "count") return table.rows.length
  if (!values.length) return 0
  if (aggregation === "avg") return values.reduce((sum, value) => sum + value, 0) / values.length
  if (aggregation === "min") return values.reduce((result, value) => Math.min(result, value), Infinity)
  if (aggregation === "max") return values.reduce((result, value) => Math.max(result, value), -Infinity)
  if (aggregation === "first") return values[0]
  if (aggregation === "last") return values[values.length - 1]
  return values.reduce((sum, value) => sum + value, 0)
}

// Match dashboard_kpi.py: numeric-first, then Unicode scalar order, independent of host locale.
export function compareDashboardCells(left: string | number | boolean | null | undefined, right: string | number | boolean | null | undefined) {
  const a = String(left ?? "")
  const b = String(right ?? "")
  const x = Number(a.replace(/,/g, ""))
  const y = Number(b.replace(/,/g, ""))
  if (Number.isFinite(x) && Number.isFinite(y)) return x - y
  const first = Array.from(a, (value) => value.codePointAt(0)!)
  const second = Array.from(b, (value) => value.codePointAt(0)!)
  for (let index = 0; index < Math.min(first.length, second.length); index++) {
    if (first[index] !== second[index]) return first[index] - second[index]
  }
  return first.length - second.length
}

export function waitForDashboardKpis(root: HTMLElement, expected: number, timeout = 30000): Promise<void> {
  return new Promise((resolve, reject) => {
    const finish = (error?: Error) => {
      observer.disconnect()
      clearTimeout(timer)
      if (error) reject(error)
      else resolve()
    }
    const check = () => {
      const states = Array.from(root.querySelectorAll<HTMLElement>("[data-kpi-state]"))
      if (states.some((item) => ["error", "invalid", "missing"].includes(item.dataset.kpiState || ""))) {
        finish(new Error("kpi_export_failed"))
        return
      }
      if (states.length >= expected && states.every((item) => ["ready", "unavailable"].includes(item.dataset.kpiState || ""))) finish()
    }
    const observer = new MutationObserver(check)
    const timer = setTimeout(() => finish(new Error("kpi_export_timeout")), timeout)
    observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ["data-kpi-state"] })
    check()
  })
}

export function dashboardWidgetHeight(widget: { type?: string; layout: { h: number }; style?: DashboardKpiStyle }) {
  return Math.max(widget.layout.h, widget.type === "kpi" && widget.style?.target ? 180 : 0)
}

export function dashboardKpiPageHeight(height: number | undefined, widgets: Array<{ type?: string; layout: { y: number; h: number }; style?: DashboardKpiStyle }>) {
  return widgets.reduce((result, widget) => widget.type === "kpi" && widget.style?.target
    ? Math.max(result, widget.layout.y + dashboardWidgetHeight(widget) + 16)
    : result, height || 794)
}

type DatasetPage = { name: string; columns: string[]; rows: DashboardKpiTable["rows"]; row_count: number; data_version?: string }

export type DatasetPaging = { unsupported: boolean }

// Pages of one dataset. An API that ignores `offset` (older leshabank) answers every offset with the first page again:
// that throws "dataset_offset_unsupported" and is remembered in `paging`, so later datasets don't ask for a second page
// that would only repeat the first (one request each). A page that carries `data_version` comes from an API that pages
// (leshabank since 37f2a2b), so a repeated first row there is data (duplicate rows), not an ignored offset.
export function datasetPager(fetchPage: (offset: number) => Promise<DatasetPage>, paging: DatasetPaging = { unsupported: false }) {
  let first = ""
  return async (offset: number) => {
    if (offset && paging.unsupported) throw new Error("dataset_offset_unsupported")
    const page = await fetchPage(offset)
    if (page.data_version) return page
    const head = JSON.stringify(page.rows[0] ?? null)
    if (offset === 0) first = head
    else if (head === first) {
      paging.unsupported = true
      throw new Error("dataset_offset_unsupported")
    }
    return page
  }
}

// One load per dataset, shared by its KPIs and by the tables and charts bound to it. `kpi` is every row or an error (a
// KPI is never computed from part of a dataset). `table` is what tables and charts show: every row, or the first page
// when the rest can't be read (an API that ignores `offset` makes a dataset over one page throw
// "dataset_offset_unsupported"). The first page is fetched once and reused; no third request.
export async function loadDashboardDataset(
  datasetID: string,
  fetchPage: (offset: number) => Promise<DatasetPage>,
): Promise<{ kpi: DashboardKpiDataset; table?: DashboardKpiTable }> {
  let first: DatasetPage
  try {
    first = await fetchPage(0)
  } catch {
    return { kpi: { status: "error" } }
  }
  const preview = { datasetID, name: first.name, headers: first.columns, rows: first.rows }
  try {
    const rows = [...first.rows]
    while (rows.length < first.row_count) {
      const page = await fetchPage(rows.length)
      // A changing or incomplete dataset must not be shown as a complete KPI.
      if (!page.rows.length || page.row_count !== first.row_count || JSON.stringify(page.columns) !== JSON.stringify(first.columns)) return { kpi: { status: "error" }, table: preview }
      rows.push(...page.rows)
    }
    if (rows.length !== first.row_count) return { kpi: { status: "error" }, table: preview }
    const table = { datasetID, name: first.name, headers: first.columns, rows }
    return { kpi: { status: "ready", table }, table }
  } catch {
    return { kpi: { status: "error" }, table: preview }
  }
}

export async function loadDashboardKpiDataset(datasetID: string, fetchPage: (offset: number) => Promise<DatasetPage>): Promise<DashboardKpiDataset> {
  return (await loadDashboardDataset(datasetID, fetchPage)).kpi
}
